import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, FormControl, InputLabel, MenuItem, Select,
  Typography, IconButton, Alert, CircularProgress, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TablePagination, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon,
  ContentCopy as DuplicateIcon, CheckCircle as ActivateIcon, Cancel as DeactivateIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import millingApi, { MillingRecipe, RecipeStatus } from '../../services/millingApi';

const statusColors: Record<RecipeStatus, 'default' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  active: 'success',
  inactive: 'warning',
  obsolete: 'error',
};

const MillingRecipeList: React.FC = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<MillingRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [duplicateDialog, setDuplicateDialog] = useState<{ open: boolean; recipe: MillingRecipe | null }>({ open: false, recipe: null });
  const [newRecipeCode, setNewRecipeCode] = useState('');

  useEffect(() => {
    fetchRecipes();
  }, [filterStatus, page, rowsPerPage]);

  const fetchRecipes = async () => {
    try {
      setLoading(true);
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const response = await millingApi.getRecipes(params);
      setRecipes(response.data || response);
      setTotal(response.total || response.length);
    } catch (err) {
      console.error(err);
      setError('Error loading recipes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchRecipes();

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this recipe?')) return;
    try {
      await millingApi.deleteRecipe(id);
      fetchRecipes();
    } catch (err) {
      alert('Error deleting recipe');
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateDialog.recipe || !newRecipeCode) return;
    try {
      await millingApi.duplicateRecipe(duplicateDialog.recipe.id!, { newRecipeCode, createdBy: 'admin' });
      setDuplicateDialog({ open: false, recipe: null });
      setNewRecipeCode('');
      fetchRecipes();
    } catch (err) {
      alert('Error duplicating recipe');
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await millingApi.activateRecipe(id, 'admin');
      fetchRecipes();
    } catch (err) {
      alert('Error activating recipe');
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await millingApi.deactivateRecipe(id, 'admin');
      fetchRecipes();
    } catch (err) {
      alert('Error deactivating recipe');
    }
  };

  if (loading && recipes.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Milling Recipes</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/milling/recipes/new')}>
          New Recipe
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search recipes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 250 }}
            />
            <IconButton onClick={handleSearch}><SearchIcon /></IconButton>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="obsolete">Obsolete</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Recipe Code</TableCell>
                <TableCell>Recipe Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Input Item</TableCell>
                <TableCell>Output Item</TableCell>
                <TableCell align="right">Yield %</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recipes.map((recipe) => (
                <TableRow key={recipe.id} hover>
                  <TableCell><Typography fontWeight="bold">{recipe.recipeCode}</Typography></TableCell>
                  <TableCell>{recipe.recipeName}</TableCell>
                  <TableCell>
                    <Chip label={recipe.status} color={statusColors[recipe.status]} size="small" />
                  </TableCell>
                  <TableCell>{recipe.inputItemName}</TableCell>
                  <TableCell>{recipe.outputItemName}</TableCell>
                  <TableCell align="right">{recipe.expectedYieldPercent?.toFixed(1)}%</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View"><IconButton size="small" onClick={() => navigate(`/milling/recipes/${recipe.id}`)}><ViewIcon /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/milling/recipes/${recipe.id}/edit`)}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Duplicate"><IconButton size="small" onClick={() => setDuplicateDialog({ open: true, recipe })}><DuplicateIcon /></IconButton></Tooltip>
                    {recipe.status === 'draft' && <Tooltip title="Activate"><IconButton size="small" color="success" onClick={() => handleActivate(recipe.id!)}><ActivateIcon /></IconButton></Tooltip>}
                    {recipe.status === 'active' && <Tooltip title="Deactivate"><IconButton size="small" color="warning" onClick={() => handleDeactivate(recipe.id!)}><DeactivateIcon /></IconButton></Tooltip>}
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(recipe.id!)}><DeleteIcon /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {recipes.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">No recipes found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Card>

      <Dialog open={duplicateDialog.open} onClose={() => setDuplicateDialog({ open: false, recipe: null })}>
        <DialogTitle>Duplicate Recipe</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Creating a copy of: {duplicateDialog.recipe?.recipeName}
          </Typography>
          <TextField
            fullWidth
            label="New Recipe Code"
            value={newRecipeCode}
            onChange={(e) => setNewRecipeCode(e.target.value)}
            placeholder="Enter new recipe code"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialog({ open: false, recipe: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleDuplicate} disabled={!newRecipeCode}>Duplicate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MillingRecipeList;
